import React, { FC, useRef, useState } from 'react';
import {
  Typography,
  Stack,
  Divider,
  ButtonBase,
  CircularProgress,
} from '@mui/material';
import Popup from 'src/components/content/popup/popup';
import ContentWrapper from 'src/components/content/content-wrapper';
import AfterpayIcon from 'src/assets/icons/afterpay.svg';
import { PaymentMethod, Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

import styles from './modal.module.scss';
import ContentSection from 'src/components/content/content-section';
import colors from 'src/styles/equalution/colors';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { Button } from 'reactstrap';
import { setIsPersonalCoachAddonAdded } from 'src/store/payment';
import { ShippingAddressForm } from 'src/pages/payment-new/components/address-fields/ShippingAddressFields';
import { ProteinFlavorField } from 'src/pages/payment-new/components/address-fields/ProteinFlavorField';
import { setProteinFlavorValue } from 'src/store/signup';
import PaymentPopup from 'src/pages/account/sections/subscription/update-payment/update-payment-popup';
import { PaymentMethodSelector } from '../components/PaymentMethodSelector';
import { usePaymentMethodState } from '../hooks/usePaymentMethodState';
import { isCardPaymentMethod } from '../utils/challengeAddon.util';

interface Props {
  onClose: () => void;
  handleCTAClick: () => void;
  isLoading: boolean;
  paymentMethod?: PaymentMethod;
  stripePromise: Promise<Stripe | null>;
  setPaymentMethod: (paymentMethod: PaymentMethod) => void;
  onPaymentTypeChange?: (type: 'card' | 'afterpay') => void;
}

export const ShippingAddressModal: FC<Props> = ({
  onClose,
  handleCTAClick,
  isLoading,
  paymentMethod,
  stripePromise,
  setPaymentMethod,
  onPaymentTypeChange,
}) => {
  const dispatch = useAppDispatch();

  const shippingAddressFormRef = useRef(null);

  const [submitDisabled, setSubmitDisabled] = useState(true);

  const {
    selectedPaymentType,
    paymentPopupVisible,
    setPaymentPopupVisible,
    handlePaymentTypeChange,
    handlePaymentMethodUpdate,
    handleAddCardClick,
  } = usePaymentMethodState({
    initialPaymentMethod: paymentMethod,
    onPaymentTypeChange,
    shouldNotifyParent: true,
  });
  const { user } = useAppSelector(state => state.auth);

  const {
    challengeAddOnProduct,
    isPersonalCoachingAdded,
    personalCoachPriceAmount,
  } = useAppSelector(state => state.payment);
  const { proteinFlavor } = useAppSelector(state => state.signUp);

  const total =
    (challengeAddOnProduct?.amount ?? 0) +
    (isPersonalCoachingAdded ? personalCoachPriceAmount ?? 0 : 0);

  const handleValidityChange = (isValid: boolean) => {
    setSubmitDisabled(!isValid);
  };

  return (
    <>
      <Popup onClose={onClose}>
        <ContentWrapper className={styles.background}>
          <ContentSection
            className={styles.shippingAddressContent}
            collapseAt="sm">
            <Button className={styles.close} onClick={onClose} />

            <Typography
              mt={3}
              fontSize={32}
              fontWeight={700}
              color={colors.OFF_BLACK}
              lineHeight={'42px'}
              textAlign="center"
              paddingX={'32px'}
              gutterBottom>
              Delivery Details
            </Typography>

            <Stack mt={2}>
              <ProteinFlavorField
                value={proteinFlavor}
                handleChange={value => dispatch(setProteinFlavorValue(value))}
              />
            </Stack>

            <Stack alignItems="center" mt={4}>
              <ShippingAddressForm
                onValidityChange={handleValidityChange}
                formRef={shippingAddressFormRef}
              />
            </Stack>

            <PaymentMethodSelector
              isLoading={isLoading}
              selectedPaymentType={selectedPaymentType}
              onPaymentTypeChange={handlePaymentTypeChange}
              paymentMethod={paymentMethod}
              onAddCardClick={handleAddCardClick}
            />

            <Divider sx={{ my: 3 }} />

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}>
              <Typography fontWeight={600}>
                {challengeAddOnProduct?.name}
              </Typography>
              <Typography fontWeight={600} fontSize={20} color={colors.SALAD}>
                ${challengeAddOnProduct?.amount.toFixed(0)}
              </Typography>
            </Stack>

            {isPersonalCoachingAdded && (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}>
                <Typography>1:1 coaching support</Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <ButtonBase
                    onClick={() => {
                      if (isLoading) {
                        return;
                      }
                      dispatch(setIsPersonalCoachAddonAdded(false));
                    }}>
                    <Typography
                      variant="button"
                      sx={{
                        fontSize: '14px',
                        textTransform: 'unset',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        color: colors.SALAD,
                        fontWeight: 600,
                        textAlign: 'right',
                      }}>
                      Remove
                    </Typography>
                  </ButtonBase>
                  <Typography
                    fontSize={20}
                    fontWeight={600}
                    color={colors.SALAD}>
                    ${personalCoachPriceAmount?.toFixed(0)}
                  </Typography>
                </Stack>
              </Stack>
            )}

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 3 }}>
              <Typography fontWeight={600}>Total</Typography>
              <Typography fontWeight={600} fontSize={20} color={colors.SALAD}>
                ${total.toFixed(0)}
              </Typography>
            </Stack>

            <Stack alignItems={'center'}>
              <Button
                disabled={
                  isLoading ||
                  submitDisabled ||
                  !proteinFlavor ||
                  (selectedPaymentType === 'card' &&
                    !isCardPaymentMethod(paymentMethod))
                }
                style={{
                  width: '400px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
                onClick={() => handleCTAClick()}>
                <>
                  {isLoading ? (
                    <CircularProgress
                      color="success"
                      size={30}
                      sx={{ color: colors.WHITE }}
                    />
                  ) : (
                    <>
                      {selectedPaymentType === 'afterpay' && (
                        <img
                          src={AfterpayIcon}
                          alt="Afterpay"
                          style={{ width: '20px', height: '20px' }}
                        />
                      )}
                      Finalise payment
                    </>
                  )}
                </>
              </Button>
            </Stack>
          </ContentSection>
        </ContentWrapper>
      </Popup>

      {paymentPopupVisible && !!user && (
        <Elements stripe={stripePromise}>
          <PaymentPopup
            user={user}
            setPaymentMethod={method =>
              handlePaymentMethodUpdate(method, setPaymentMethod)
            }
            setPopupVisible={setPaymentPopupVisible}
          />
        </Elements>
      )}
    </>
  );
};
